import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import type { ModalidadContacto, ObjetivoVisita } from '../../services/visitas.service';
import type { RutaDiaItemLista } from '../../services/ruta-dia-storage.service';
import { Capacitor } from '@capacitor/core';
import { RutaDiaStorageService } from '../../services/ruta-dia-storage.service';
import { ToastService } from '../../services/toast.service';
import { CameraGeolocationService } from '../../services/camera-geolocation.service';
import { FILE_UPLOAD_ACCEPT, validateUploadFile } from '../../utils/file-upload-constraints';
import { aplicarEntradaFolio, folioRegistroValidators, normalizarFolioInput } from '../../utils/folio.util';
import {
  ID_MODALIDAD_CONTACTO_PRESENCIAL,
  MAX_OBJETIVOS_VISITA_REGISTRO,
  normalizarIdsObjetivoVisita,
  objetivosRegistroValidators,
} from '../../utils/visita-registro.util';

@Component({
  selector: 'app-formulario-registrar-visita-offline',
  templateUrl: './formulario-registrar-visita-offline.component.html',
  styleUrls: ['./formulario-registrar-visita-offline.component.scss'],
  standalone: false,
})
export class FormularioRegistrarVisitaOfflineComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) row!: RutaDiaItemLista;
  @Output() readonly guardado = new EventEmitter<void>();
  @Output() readonly cerrarSolicitado = new EventEmitter<void>();

  @ViewChild('contenidoScroll') contenidoScroll?: ElementRef<HTMLElement>;

  private tipoReporteSub?: Subscription;
  private generarFolioSub?: Subscription;

  private readonly fb = inject(FormBuilder);
  private readonly storage = inject(RutaDiaStorageService);
  private readonly toast = inject(ToastService);
  private readonly cameraGeo = inject(CameraGeolocationService);

  readonly fileUploadAccept = FILE_UPLOAD_ACCEPT;
  readonly esPlataformaNativa = Capacitor.isNativePlatform();

  /** Sufijo único por fila para ion-datetime id (UUID puede tener guiones; se normaliza). */
  idSuffix = 'x';

  get idGenerarFolioInfo(): string {
    return `reg-off-generar-folio-info-${this.idSuffix}`;
  }

  formularioRegistro: FormGroup = this.fb.group({
    tipo_reporte: [null as 'digital' | 'papel' | null, Validators.required],
    id_modalidad_contacto: [null as number | null],
    id_objetivo_visita: [[] as number[]],
    generar_folio: [false],
    folio: [''],
    recomendaciones: [''],
    compromisos: [''],
    desarrollo_asesoria: ['', Validators.required],
  });

  fechaVisitaRegistroISO: string | null = null;
  horaInicioRegistro: string | null = null;
  horaTerminoRegistro: string | null = null;

  itemsRegistroArchivos: { file: File | null; id_categoria: number | null }[] = [];

  objetivosVisita: ObjetivoVisita[] = [];
  modalidadesContacto: ModalidadContacto[] = [];
  categoriasAnexo: { id: number; descripcion: string }[] = [];
  bolsaVisita = false;
  readonly maxObjetivosVisitaRegistro = MAX_OBJETIVOS_VISITA_REGISTRO;

  guardando = false;

  /** Si existe borrador local para esta visita, se reutiliza este id al guardar (sin duplicar pendientes). */
  pendienteIdReutilizable: string | null = null;
  /** Conservado al rehidratar para no alterar el orden temporal en listados. */
  private pendienteCreadoEnOriginal: string | null = null;
  private restaurarGen = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['row']?.currentValue?.id) {
      this.idSuffix = String(this.row.id).replace(/[^a-zA-Z0-9]/g, '');
      const prev = changes['row'].previousValue?.id as string | undefined;
      const cur = this.row.id;
      if (prev !== undefined && prev !== cur) {
        void this.restaurarBorradorDesdePendienteSiExiste();
      }
    }
  }

  private async cargarCatalogos(): Promise<void> {
    this.objetivosVisita = await this.storage.obtenerObjetivosVisitaLocal();
    this.modalidadesContacto = await this.storage.obtenerModalidadesContactoLocal();
    this.categoriasAnexo = await this.storage.obtenerCategoriasAnexoLocal();
  }

  private async cargarBolsaVisitaDesdeLocal(): Promise<void> {
    const idVisita = this.row?.id;
    if (!idVisita) {
      this.bolsaVisita = false;
      return;
    }
    const visita = await this.storage.obtenerVisitaLocal(idVisita);
    this.bolsaVisita = visita?.bolsa_visita === true;
  }

  async ngOnInit(): Promise<void> {
    if (this.row?.id) {
      this.idSuffix = String(this.row.id).replace(/[^a-zA-Z0-9]/g, '');
    }
    await this.cargarCatalogos();
    await this.cargarBolsaVisitaDesdeLocal();
    this.itemsRegistroArchivos = [];
    this.tipoReporteSub = this.formularioRegistro
      .get('tipo_reporte')
      ?.valueChanges.subscribe(() => this.aplicarValidadoresRegistroPorTipo());
    this.generarFolioSub = this.formularioRegistro
      .get('generar_folio')
      ?.valueChanges.subscribe(() => this.aplicarValidadoresFolioRegistro());
    this.aplicarValidadoresRegistroPorTipo();
    await this.restaurarBorradorDesdePendienteSiExiste();
  }

  ngOnDestroy(): void {
    this.tipoReporteSub?.unsubscribe();
    this.generarFolioSub?.unsubscribe();
  }

  private aplicarValidadoresRegistroPorTipo(): void {
    const tipo = this.formularioRegistro.get('tipo_reporte')?.value as 'digital' | 'papel' | null;
    const modalidadCtrl = this.formularioRegistro.get('id_modalidad_contacto');
    const idCtrl = this.formularioRegistro.get('id_objetivo_visita');
    const recCtrl = this.formularioRegistro.get('recomendaciones');
    const compCtrl = this.formularioRegistro.get('compromisos');
    if (tipo === 'digital' || tipo === 'papel') {
      modalidadCtrl?.setValidators([Validators.required]);
      idCtrl?.setValidators(objetivosRegistroValidators());
    } else {
      modalidadCtrl?.clearValidators();
      idCtrl?.clearValidators();
    }
    if (tipo === 'digital') {
      recCtrl?.setValidators([Validators.required, Validators.maxLength(500)]);
      compCtrl?.setValidators([Validators.required, Validators.maxLength(500)]);
    } else {
      recCtrl?.setValidators([Validators.maxLength(500)]);
      compCtrl?.setValidators([Validators.maxLength(500)]);
    }
    modalidadCtrl?.updateValueAndValidity({ emitEvent: false });
    idCtrl?.updateValueAndValidity({ emitEvent: false });
    recCtrl?.updateValueAndValidity({ emitEvent: false });
    compCtrl?.updateValueAndValidity({ emitEvent: false });
    this.aplicarValidadoresFolioRegistro();
  }

  esFolioRegistroManualRequerido(): boolean {
    const tipo = this.formularioRegistro.get('tipo_reporte')?.value;
    return (tipo === 'digital' || tipo === 'papel') && !this.esGenerarFolioRegistroActivo();
  }

  esFolioRegistroEditable(): boolean {
    return !this.esGenerarFolioRegistroActivo();
  }

  private esGenerarFolioRegistroActivo(): boolean {
    return this.formularioRegistro.get('generar_folio')?.value === true;
  }

  private aplicarValidadoresFolioRegistro(): void {
    const folioCtrl = this.formularioRegistro.get('folio');
    const generar = this.esGenerarFolioRegistroActivo();
    const tipo = this.formularioRegistro.get('tipo_reporte')?.value as 'digital' | 'papel' | null;
    if (generar) {
      folioCtrl?.clearValidators();
      folioCtrl?.setValue('', { emitEvent: false });
      folioCtrl?.disable({ emitEvent: false });
    } else {
      folioCtrl?.enable({ emitEvent: false });
      if (tipo === 'digital' || tipo === 'papel') {
        folioCtrl?.setValidators(folioRegistroValidators());
      } else {
        folioCtrl?.clearValidators();
      }
    }
    folioCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  private aplicarModalidadContactoSegunBolsa(): void {
    const ctrl = this.formularioRegistro.get('id_modalidad_contacto');
    if (!ctrl) return;
    if (this.bolsaVisita) {
      ctrl.enable({ emitEvent: false });
      return;
    }
    ctrl.setValue(ID_MODALIDAD_CONTACTO_PRESENCIAL, { emitEvent: false });
    ctrl.disable({ emitEvent: false });
  }

  esModalidadContactoRegistroEditable(): boolean {
    return this.bolsaVisita;
  }

  readonly compareIdModalidadContacto = (
    o1: number | string | null | undefined,
    o2: number | string | null | undefined
  ): boolean => {
    if (o1 == null && o2 == null) return true;
    if (o1 == null || o2 == null || o1 === '' || o2 === '') return false;
    return Number(o1) === Number(o2);
  };

  getTipoReporteRegistro(): 'digital' | 'papel' | null {
    const v = this.formularioRegistro.get('tipo_reporte')?.value;
    return v === 'digital' || v === 'papel' ? v : null;
  }

  onFolioInput(event: CustomEvent): void {
    if (!this.esFolioRegistroEditable()) return;
    aplicarEntradaFolio(this.formularioRegistro.get('folio'), event.detail?.value as string);
  }

  /** Fecha/hora agendada legible para mensajes de validación. */
  textoReferenciaAgendaFormateado(): string {
    if (!this.row?.fechaIso) return '';
    const d = new Date(this.row.fechaIso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /** Fecha y hora de realización anteriores a la visita agendada (regla que deshabilita Completar). */
  registroFechaHoraBloqueadoPorAgenda(): boolean {
    const fechaReg = this.fechaVisitaRegistroISO;
    const horaInicio = this.horaInicioRegistro;
    if (!fechaReg || !horaInicio || !this.row?.fechaIso) return false;
    return this.esFechaHoraAnteriorA(fechaReg, horaInicio, this.row.fechaIso, true);
  }

  /** Hay fecha pero la hora de inicio no quedó confirmada en el modelo (p. ej. sin abrir el selector). */
  faltaConfirmarHoraInicioModelo(): boolean {
    return !!this.fechaVisitaRegistroISO && !this.horaInicioRegistro;
  }

  private inicializarFechaHoraDesdeFila(): void {
    if (!this.row?.fechaIso) return;
    const d = new Date(this.row.fechaIso);
    if (Number.isNaN(d.getTime())) return;
    const inicio = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    this.fechaVisitaRegistroISO = inicio.toISOString();
    this.horaInicioRegistro = this.extraerHoraDeISO(this.row.fechaIso);
    const minTerm = this.obtenerHoraInicioMasUnaHora(this.horaInicioRegistro);
    this.horaTerminoRegistro = minTerm;
  }

  private resetFormularioRegistroVacio(): void {
    this.formularioRegistro.reset(
      {
        tipo_reporte: null,
        id_modalidad_contacto: null,
        id_objetivo_visita: [],
        generar_folio: false,
        folio: '',
        recomendaciones: '',
        compromisos: '',
        desarrollo_asesoria: '',
      },
      { emitEvent: false }
    );
    this.aplicarModalidadContactoSegunBolsa();
  }

  /**
   * Carga pendiente `registrar_visita` local para esta fila o deja valores nuevos desde la agenda.
   */
  private async restaurarBorradorDesdePendienteSiExiste(): Promise<void> {
    const gen = ++this.restaurarGen;
    const idVisita = this.row?.id;
    if (!idVisita) return;

    this.pendienteIdReutilizable = null;
    this.pendienteCreadoEnOriginal = null;
    this.fechaVisitaRegistroISO = null;
    this.horaInicioRegistro = null;
    this.horaTerminoRegistro = null;
    this.itemsRegistroArchivos = [];
    this.resetFormularioRegistroVacio();

    await this.cargarBolsaVisitaDesdeLocal();
    const p = await this.storage.obtenerPendienteRegistrarVisitaMasReciente(idVisita);
    if (gen !== this.restaurarGen) return;

    if (!p) {
      this.inicializarFechaHoraDesdeFila();
      this.aplicarValidadoresRegistroPorTipo();
      this.aplicarModalidadContactoSegunBolsa();
      return;
    }

    this.pendienteIdReutilizable = p.id;
    this.pendienteCreadoEnOriginal = p.creadoEn ?? null;

    const idsObjetivo = p.idObjetivosVisita?.length
      ? normalizarIdsObjetivoVisita(p.idObjetivosVisita)
      : normalizarIdsObjetivoVisita(p.idObjetivoVisita ?? null);
    const idModalidad =
      p.idModalidadContacto ??
      (this.bolsaVisita ? null : ID_MODALIDAD_CONTACTO_PRESENCIAL);

    this.formularioRegistro.patchValue(
      {
        tipo_reporte: p.tipoReporte ?? null,
        id_modalidad_contacto: idModalidad,
        id_objetivo_visita: idsObjetivo,
        generar_folio: p.generarFolio === true,
        folio: p.generarFolio === true ? '' : normalizarFolioInput(p.folio),
        recomendaciones: p.recomendaciones ?? '',
        compromisos: p.compromisos ?? '',
        desarrollo_asesoria: p.desarrolloAsesoria ?? '',
      },
      { emitEvent: false }
    );
    this.aplicarModalidadContactoSegunBolsa();

    const fv = p.fechaVisitaRegistro?.trim();
    if (fv) {
      const partes = fv.split('-').map(s => parseInt(s, 10));
      const y = partes[0];
      const mo = partes[1];
      const d = partes[2];
      if (y > 0 && mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
        const inicio = new Date(y, mo - 1, d);
        this.fechaVisitaRegistroISO = inicio.toISOString();
      }
    }
    this.horaInicioRegistro = p.horaInicioRegistro ?? null;
    this.horaTerminoRegistro = p.horaTerminoRegistro ?? null;

    const blobs = await this.storage.obtenerBlobsRegistroPendiente(p.id);
    if (gen !== this.restaurarGen) return;

    this.itemsRegistroArchivos = blobs.map(b => ({
      file: new File([b.data], b.nombre, { type: b.mimeType || 'application/octet-stream' }),
      id_categoria: b.idCategoria,
    }));

    if (this.itemsRegistroArchivos.length === 0 && (p.tipoReporte === 'papel' || (p.archivosCount ?? 0) > 0)) {
      this.itemsRegistroArchivos = [{ file: null, id_categoria: null }];
    }

    this.aplicarValidadoresRegistroPorTipo();
  }

  async adjuntarDesdeCamaraNativo(index: number, source: 'camera' | 'gallery'): Promise<void> {
    if (!this.esPlataformaNativa) return;
    try {
      const file = await this.cameraGeo.obtenerFotoComoArchivo(source);
      const check = validateUploadFile(file);
      if (!check.valid) {
        await this.toast.mostrar(check.message, 'warning');
        return;
      }
      if (index >= 0 && index < this.itemsRegistroArchivos.length) {
        this.itemsRegistroArchivos = this.itemsRegistroArchivos.map((item, i) =>
          i === index ? { ...item, file } : item
        );
      }
    } catch (e) {
      console.error(e);
      await this.toast.mostrar('No se pudo obtener la imagen.', 'warning');
    }
  }

  getFechaMinimaRegistroISO(): string {
    const ref = this.row?.fechaIso;
    if (ref) {
      const d = new Date(ref);
      if (!Number.isNaN(d.getTime())) {
        const inicio = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        return inicio.toISOString();
      }
    }
    const hoy = new Date();
    return new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()).toISOString();
  }

  onFechaRegistroChange(event: CustomEvent): void {
    const v = (event as any).detail?.value;
    this.fechaVisitaRegistroISO = v || null;
  }

  private extraerHoraDeISO(iso: string): string {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  private obtenerHoraInicioMasUnaHora(horaHHmm: string): string {
    const [h, m] = horaHHmm.split(':').map(s => parseInt(s, 10) || 0);
    const d = new Date(0, 0, 0, h, m, 0, 0);
    d.setHours(d.getHours() + 1);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  getHoraTerminoRegistroValueISO(): string | undefined {
    if (!this.fechaVisitaRegistroISO || !this.horaTerminoRegistro) return undefined;
    const parteFecha = this.fechaVisitaRegistroISO.split('T')[0];
    return `${parteFecha}T${this.horaTerminoRegistro}:00.000`;
  }

  getHoraTerminoMinimaRegistroISO(): string | undefined {
    if (!this.fechaVisitaRegistroISO || !this.horaInicioRegistro) return undefined;
    const parteFecha = this.fechaVisitaRegistroISO.split('T')[0];
    const horaMin = this.obtenerHoraInicioMasUnaHora(this.horaInicioRegistro);
    return `${parteFecha}T${horaMin}:00.000`;
  }

  private esHoraAnteriorA(a: string, b: string): boolean {
    const [ha, ma] = a.split(':').map(s => parseInt(s, 10) || 0);
    const [hb, mb] = b.split(':').map(s => parseInt(s, 10) || 0);
    return ha < hb || (ha === hb && ma < mb);
  }

  onHoraInicioRegistroChange(event: CustomEvent): void {
    const valor = (event as any).detail?.value;
    if (valor) {
      const hora = String(valor).includes('T') ? this.extraerHoraDeISO(String(valor)) : String(valor);
      this.horaInicioRegistro = hora;
      const minTermino = this.getHoraTerminoMinimaRegistroISO();
      if (minTermino) {
        const horaMinTermino = this.extraerHoraDeISO(minTermino);
        if (!this.horaTerminoRegistro || this.esHoraAnteriorA(this.horaTerminoRegistro, horaMinTermino)) {
          this.horaTerminoRegistro = horaMinTermino;
        }
      }
    } else {
      this.horaInicioRegistro = null;
    }
  }

  onHoraTerminoRegistroChange(event: CustomEvent): void {
    const valor = (event as any).detail?.value;
    if (valor) {
      this.horaTerminoRegistro = String(valor).includes('T')
        ? this.extraerHoraDeISO(String(valor))
        : String(valor);
    } else {
      this.horaTerminoRegistro = null;
    }
  }

  private esFechaHoraAnteriorA(
    fechaISO: string,
    hora: string,
    fechaReferenciaISO: string,
    truncarRefAMinutos = false
  ): boolean {
    const parteFecha = fechaISO.split('T')[0];
    const [y, mo, d] = parteFecha.split('-').map(s => parseInt(s, 10) || 0);
    const partesHora = hora.split(':').map(s => parseInt(s, 10) || 0);
    const hr = partesHora[0] ?? 0;
    const min = partesHora[1] ?? 0;
    const fechaSel = new Date(y, mo - 1, d, hr, min, 0, 0);
    let fechaRef = new Date(fechaReferenciaISO);
    if (truncarRefAMinutos) {
      fechaRef = new Date(
        fechaRef.getFullYear(),
        fechaRef.getMonth(),
        fechaRef.getDate(),
        fechaRef.getHours(),
        fechaRef.getMinutes(),
        0,
        0
      );
    }
    return fechaSel.getTime() < fechaRef.getTime();
  }

  hayAdjuntoRegistroSinCategoria(): boolean {
    return (this.itemsRegistroArchivos ?? []).some(it => it?.file != null && it?.id_categoria == null);
  }

  hayAdjuntoRegistroCompleto(): boolean {
    return (this.itemsRegistroArchivos ?? []).some(it => it?.file != null && it?.id_categoria != null);
  }

  agregarFilaArchivoRegistro(): void {
    this.itemsRegistroArchivos = [...this.itemsRegistroArchivos, { file: null, id_categoria: null }];
    setTimeout(() => {
      const el = this.contenidoScroll?.nativeElement;
      el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, 80);
  }

  eliminarFilaArchivoRegistro(index: number): void {
    this.itemsRegistroArchivos = this.itemsRegistroArchivos.filter((_, i) => i !== index);
  }

  onArchivoRegistroFilaChange(index: number, event: Event): void {
    const input = event?.target as HTMLInputElement;
    const raw = input?.files?.[0] ?? null;
    if (raw) {
      const check = validateUploadFile(raw);
      if (!check.valid) {
        void this.toast.mostrar(check.message, 'warning');
        input.value = '';
        return;
      }
    }
    const file = raw;
    if (index >= 0 && index < this.itemsRegistroArchivos.length) {
      this.itemsRegistroArchivos = this.itemsRegistroArchivos.map((item, i) =>
        i === index ? { ...item, file, ...(file == null ? { id_categoria: null } : {}) } : item
      );
    }
  }

  onCategoriaRegistroFilaChange(index: number, value: number | string | null): void {
    const id = value != null && value !== '' ? Number(value) : null;
    if (index >= 0 && index < this.itemsRegistroArchivos.length) {
      this.itemsRegistroArchivos = this.itemsRegistroArchivos.map((item, i) =>
        i === index ? { ...item, id_categoria: id } : item
      );
    }
  }

  nombreArchivoRegistroFila(index: number): string {
    const item = this.itemsRegistroArchivos[index];
    return item?.file?.name ?? 'Seleccionar archivo';
  }

  private formatearFechaVisitaYYYYMMDD(fechaISO: string): string {
    const d = new Date(fechaISO);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  esFormularioValido(): boolean {
    const fechaReg = this.fechaVisitaRegistroISO;
    const horaInicio = this.horaInicioRegistro;
    if (!fechaReg || !horaInicio) return false;
    if (this.hayAdjuntoRegistroSinCategoria()) return false;
    if (!this.formularioRegistro.valid) return false;
    const tipo = this.formularioRegistro.get('tipo_reporte')?.value as 'digital' | 'papel' | null;
    if (tipo === 'papel' && !this.hayAdjuntoRegistroCompleto()) return false;
    if (this.row?.fechaIso) {
      const anterior = this.esFechaHoraAnteriorA(fechaReg, horaInicio, this.row.fechaIso, true);
      if (anterior) return false;
    }
    return true;
  }

  cerrar(): void {
    this.cerrarSolicitado.emit();
  }

  async completar(): Promise<void> {
    if (!this.esFormularioValido()) {
      if (this.registroFechaHoraBloqueadoPorAgenda()) {
        const ref = this.textoReferenciaAgendaFormateado();
        await this.toast.mostrar(
          ref
            ? `La fecha y hora de realización deben ser iguales o posteriores a la visita agendada (${ref}).`
            : 'La fecha y hora de realización deben ser iguales o posteriores a la visita agendada.',
          'warning'
        );
        return;
      }
      if (this.hayAdjuntoRegistroSinCategoria()) {
        await this.toast.mostrar('Seleccione una categoría para cada archivo adjunto.', 'warning');
        return;
      }
      if (!this.fechaVisitaRegistroISO || !this.horaInicioRegistro) {
        await this.toast.mostrar(
          'Indique la fecha y la hora de inicio. Si ya ve una hora en pantalla, ábrala en el selector y confirme para registrarla.',
          'warning'
        );
        return;
      }
      await this.toast.mostrar('Revise los campos obligatorios del formulario.', 'warning');
      return;
    }
    const raw = this.formularioRegistro.getRawValue();
    const tipo = raw.tipo_reporte as 'digital' | 'papel';
    const archivos = (this.itemsRegistroArchivos ?? [])
      .filter(it => it.file != null && it.id_categoria != null)
      .map(it => ({ file: it.file!, idCategoria: it.id_categoria! }));

    if (tipo === 'papel' && archivos.length === 0) {
      await this.toast.mostrar('Adjunte al menos un archivo con categoría (tipo de reporte papel).', 'warning');
      return;
    }

    const fechaVisita = this.formatearFechaVisitaYYYYMMDD(this.fechaVisitaRegistroISO!);
    const esActualizacion = !!this.pendienteIdReutilizable;
    const pendienteId = this.pendienteIdReutilizable ?? `offline-reg-${Date.now()}`;
    const creadoEnExistente = esActualizacion ? this.pendienteCreadoEnOriginal ?? undefined : undefined;

    this.guardando = true;
    try {
      await this.storage.agregarPendienteRegistrarVisita({
        pendienteId,
        idVisita: this.row.id,
        idProductor: this.row.idProductor,
        nombreProductor: this.row.nombreProductor,
        subtituloTipo: this.row.subtituloTipo,
        lugarVisita: this.row.lugar,
        fechaHoraVisitaAgendadaIso: this.row.fechaIso,
        desarrolloAsesoria: String(raw.desarrollo_asesoria ?? '').trim(),
        fechaVisitaRegistro: fechaVisita,
        horaInicio: this.horaInicioRegistro!,
        horaTermino: this.horaTerminoRegistro,
        tipoReporte: tipo,
        idObjetivosVisita: normalizarIdsObjetivoVisita(raw.id_objetivo_visita as number[] | null),
        idModalidadContacto: Number(
          this.formularioRegistro.getRawValue().id_modalidad_contacto ??
            ID_MODALIDAD_CONTACTO_PRESENCIAL
        ),
        bolsaVisita: this.bolsaVisita,
        generarFolio: raw.generar_folio === true,
        folio: raw.generar_folio === true ? '' : normalizarFolioInput(raw.folio),
        recomendaciones: String(raw.recomendaciones ?? ''),
        compromisos: String(raw.compromisos ?? ''),
        archivos,
        creadoEnExistente,
      });
      await this.toast.mostrar(
        esActualizacion
          ? 'Borrador actualizado en el dispositivo. Pendiente de subir con conexión.'
          : 'Visita registrada localmente. Pendiente de subir con conexión.',
        'success',
        2800
      );
      this.guardado.emit();
      this.cerrarSolicitado.emit();
    } catch (e) {
      console.error(e);
      await this.toast.mostrar('No se pudo guardar el registro offline.', 'error');
    } finally {
      this.guardando = false;
    }
  }
}
